<!-- 系统管理员、角色、日志、参数与支付配置的展示。数据请求和编辑弹窗由父页面处理。 -->
<template>
  <section class="system-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>{{ sectionHint }}</span>
        </div>
      </div>

      <div v-if="section === 'admins'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>账号</th>
              <th>昵称</th>
              <th>角色</th>
              <th>管理门店</th>
              <th>状态</th>
              <th>创建时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in admins" :key="item.id">
              <td><span class="cell-text">{{ item.username }}</span></td>
              <td><span class="cell-text">{{ item.nickname || '--' }}</span></td>
              <td><span class="cell-text">{{ item.roles?.join('、') || '--' }}</span></td>
              <td><span class="cell-text">{{ getStoreNames(item.storeIds) }}</span></td>
              <td><span :class="['status-tag', toneClass(adminStatusTone(item.status))]">{{ getAdminStatusText(item.status) }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑管理员" @click="emit('editAdmin', item)"><el-icon><EditPen /></el-icon></button><button title="删除管理员" @click="emit('removeAdmin', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!admins.length && !loadingAdmins"><td colspan="7"><el-empty description="暂无管理员数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'roles'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>角色名称</th>
              <th>角色编码</th>
              <th>数据范围</th>
              <th class="is-center">权限数量</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in roles" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ item.code || '--' }}</span></td>
              <td><span class="cell-text">{{ getDataScopeText(item.dataScope) }}</span></td>
              <td class="is-center">{{ item.permissions?.length ?? 0 }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑角色" @click="emit('editRole', item)"><el-icon><EditPen /></el-icon></button><button title="删除角色" @click="emit('removeRole', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!roles.length && !loadingRoles"><td colspan="5"><el-empty description="暂无角色数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'logs'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>操作人</th>
              <th>模块</th>
              <th>操作内容</th>
              <th>IP</th>
              <th>时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in operationLogPage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.operatorName || item.operatorId || '--' }}</span></td>
              <td><span class="cell-text">{{ item.module || '--' }}</span></td>
              <td><span class="cell-text">{{ item.action || '--' }}</span></td>
              <td><span class="cell-text">{{ item.ip || '--' }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
            </tr>
            <tr v-if="!(operationLogPage?.list.length) && !loadingOperationLogs"><td colspan="5"><el-empty description="暂无操作日志" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'params'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>参数编码</th>
              <th>参数值</th>
              <th>分组</th>
              <th>备注</th>
              <th>更新时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in systemParams" :key="item.id || item.key">
              <td><span class="cell-text">{{ item.key }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ item.value || '--' }}</span></td>
              <td><span class="cell-text">{{ item.groupName || '--' }}</span></td>
              <td><span class="cell-text">{{ item.remark || '--' }}</span></td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑参数" @click="emit('editParam', item)"><el-icon><EditPen /></el-icon></button><button title="删除参数" @click="emit('removeParam', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!systemParams.length && !loadingSystemParams"><td colspan="6"><el-empty description="暂无系统参数" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>支付渠道</th>
              <th>应用 ID</th>
              <th>商户号</th>
              <th>回调地址</th>
              <th>密钥状态</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paymentConfigs" :key="item.id">
              <td><span class="cell-text">{{ getPaymentChannelText(item.channel) }}</span></td>
              <td><span class="cell-text">{{ item.appId || '--' }}</span></td>
              <td><span class="cell-text">{{ item.mchId || '--' }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ item.notifyUrl || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(paymentConfigStatusTone(item.configStatus))]">{{ getPaymentConfigStatusText(item.configStatus) }}</span></td>
              <td><span :class="['status-tag', toneClass(paymentEnabledTone(item.enabled))]">{{ getPaymentEnabledText(item.enabled) }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="编辑支付配置" @click="emit('editPaymentConfig', item)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!paymentConfigs.length && !loadingPaymentConfigs"><td colspan="7"><el-empty description="暂无支付配置" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'admins' && adminPage" class="table-footer">
        <span>共 {{ adminPage.total }} 条记录，第 {{ adminPage.page }}/{{ adminTotalPages }} 页</span>
        <div>
          <button :disabled="adminPage.page <= 1" @click="emit('changeAdminPage', adminPage.page - 1)">上一页</button>
          <button class="is-active">{{ adminPage.page }}</button>
          <button :disabled="!adminPage.hasMore" @click="emit('changeAdminPage', adminPage.page + 1)">下一页</button>
        </div>
      </div>

      <div v-else-if="section === 'logs' && operationLogPage" class="table-footer">
        <span>共 {{ operationLogPage.total }} 条记录，第 {{ operationLogPage.page }}/{{ operationLogTotalPages }} 页</span>
        <div>
          <button :disabled="operationLogPage.page <= 1" @click="emit('changeOperationLogPage', operationLogPage.page - 1)">上一页</button>
          <button class="is-active">{{ operationLogPage.page }}</button>
          <button :disabled="!operationLogPage.hasMore" @click="emit('changeOperationLogPage', operationLogPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import { Delete, EditPen } from '@element-plus/icons-vue'
import type { AdminStore } from '@/api/store'
import type { AdminAccount, AdminRole, PaymentConfig, SystemParam } from '@/api/system'
import type { OperationStat } from '@/data/operationDesign'

type AdminPage = Awaited<ReturnType<typeof import('@/api/system').fetchAdmins>>
type OperationLogPage = Awaited<ReturnType<typeof import('@/api/system').fetchOperationLogs>>
type SystemSection = 'admins' | 'roles' | 'logs' | 'params' | 'payments'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: SystemSection
  stats: OperationStat[]
  admins: AdminAccount[]
  adminPage?: AdminPage
  storeOptions: AdminStore[]
  roles: AdminRole[]
  operationLogPage?: OperationLogPage
  systemParams: SystemParam[]
  paymentConfigs: PaymentConfig[]
  loadingAdmins: boolean
  loadingRoles: boolean
  loadingOperationLogs: boolean
  loadingSystemParams: boolean
  loadingPaymentConfigs: boolean
  adminTotalPages: number
  operationLogTotalPages: number
  getAdminStatusText: (status?: string) => string
  getDataScopeText: (scope?: string) => string
  getPaymentChannelText: (channel?: string) => string
  getPaymentConfigStatusText: (status?: string) => string
  getPaymentEnabledText: (enabled?: boolean) => string
  adminStatusTone: (status?: string) => StatusTone
  paymentConfigStatusTone: (status?: string) => StatusTone
  paymentEnabledTone: (enabled?: boolean) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  changeAdminPage: [page: number]
  changeOperationLogPage: [page: number]
  editAdmin: [item: AdminAccount]
  editRole: [item: AdminRole]
  editParam: [item: SystemParam]
  editPaymentConfig: [item: PaymentConfig]
  removeAdmin: [item: AdminAccount]
  removeRole: [item: AdminRole]
  removeParam: [item: SystemParam]
}>()

const {
  section,
  stats,
  admins,
  adminPage,
  storeOptions,
  roles,
  operationLogPage,
  systemParams,
  paymentConfigs,
  loadingAdmins,
  loadingRoles,
  loadingOperationLogs,
  loadingSystemParams,
  loadingPaymentConfigs,
  adminTotalPages,
  operationLogTotalPages,
  getAdminStatusText,
  getDataScopeText,
  getPaymentChannelText,
  getPaymentConfigStatusText,
  getPaymentEnabledText,
  adminStatusTone,
  paymentConfigStatusTone,
  paymentEnabledTone,
  toneClass,
} = toRefs(props)

const storeNameMap = computed(() => new Map(storeOptions.value.map((store) => [store.id, store.name])))

function getStoreNames(storeIds?: string[]): string {
  if (!storeIds?.length) return '全部门店'
  return storeIds.map((storeId) => storeNameMap.value.get(storeId) ?? storeId).join('、')
}

const sectionHint = computed(() => {
  if (section.value === 'admins') return '管理员账号支持真实分页、新增和编辑，密码仅在提交时传输。'
  if (section.value === 'roles') return '角色权限支持真实查询、新增和编辑，权限编码通过多选框保存。'
  if (section.value === 'logs') return '操作日志支持真实分页查询，当前后端暂未提供操作人和模块筛选参数。'
  if (section.value === 'params') return '系统参数支持真实查询与单项新增、编辑，保存后按参数编码写回后端。'
  return '支付配置只展示脱敏信息，密钥留空表示更新时保持原值。'
})

</script>

<style scoped lang="scss">
.system-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.is-center { text-align: center; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-text--wrap { max-width: 320px; white-space: normal; word-break: break-all; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
